import { truncate } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Characteristics } from '@workspace/ui/components/Characteristics';
import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import { RecommendationSource } from '~/api/Catalog';
import { company } from '~/company/Company';
import { createCategoryUrl } from '~/components/CategoryBlocks/utils';
import CollapsibleText from '~/components/CollapsibleText/CollapsibleText';
import {
  type BreadcrumbItem,
  Breadcrumbs,
} from '~/components/isolated/Breadcrumbs';
import { ModalType } from '~/components/Modal/interface';
import { useGlobal } from '~/hooks/useGlobal';
import { useIsRN } from '~/hooks/useIsRN';
import { useModal } from '~/hooks/useModal';
import { useStatusBarControl } from '~/hooks/useStatusBarControl';
import NotFoundV3 from '~/pages/NotFoundV3';
import { getProductRatingData } from '~/pages/Product/shared/getProductRatingData';
import { useProductNutritionList } from '~/pages/Product/shared/useProductNutritionList';
import { catalogStore } from '~/stores';
import { filterNutritionFields, hasPropertyValue } from '~/types/Product';

import type { ProductViewProps } from '../../Product';
import { AddToCardButton } from './components/AddToCardButton';
import { ListOfItems } from './components/ListOfItems';
import { MiscButtons } from './components/MiscButtons';
import { ProductBadges } from './components/ProductBadges';
import { ProductCustomsInfo } from './components/ProductCustomsInfo';
import { ProductImageGallery } from './components/ProductImageGallery';
import { ProductInfoBadges } from './components/ProductInfoBadges';
import ProductMultibuyLevels from './components/ProductMultibuyLevels';
import { ProductPickupEta } from './components/ProductPickupEta';
import { ProductPrice } from './components/ProductPrice';
import { ProductProperties } from './components/ProductProperties';
import { ProductRatingSummary } from './components/ProductRatingSummary';
import { ProductRecommendations } from './components/ProductRecommendations';
import { ProductReviews } from './components/ProductReviews';
import { ProductTitle } from './components/ProductTitle';
import { ProductUspTiles } from './components/ProductUspTiles';
import { Tabs } from './components/Tabs';
import type { TabItem } from './components/Tabs/interfaces';
import { PRODUCT_TABS } from './constants';
import styles from './ProductV3.module.scss';

const ProductV3 = ({
  currentItem,
  product,
  isLoading,
  isBundle,
  bundleImages,
  title,
  isExpress,
  handleChangeOffer,
  handleChangeCount,
  count,
  limit,
  propertyList,
  badgeList,
  handleFavorite,
  isMultiBuy,
  currentPrice,
}: ProductViewProps) => {
  const { isMobile } = useGlobal();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { openModal } = useModal();
  const isRN = useIsRN();

  const isMCosmetic = company.is('mcosmetic');

  const handleCreateReview = useCallback(() => {
    if (!product.data) return;
    catalogStore.feedbackProduct = product.data;
    openModal(ModalType.CreateReview);
  }, [product.data, openModal]);

  const compositionProperty = useMemo(
    () =>
      isMCosmetic ? propertyList.find((p) => p.code === 'sostav') : undefined,
    [propertyList, isMCosmetic],
  );

  const applicationProperty = useMemo(
    () =>
      isMCosmetic
        ? propertyList.find((p) => p.code === 'primenenie')
        : undefined,
    [propertyList, isMCosmetic],
  );

  const characteristicsList = useMemo(
    () =>
      filterNutritionFields(propertyList)
        .filter(hasPropertyValue)
        .filter(
          (p) =>
            !(
              isMCosmetic &&
              [
                'sostav',
                'primenenie',
                'kod_upakovki',
                'ikpu',
                'package_code',
                'spic',
              ].includes(p.code)
            ),
        ),
    [propertyList, isMCosmetic],
  );

  const nutritionList = useProductNutritionList(propertyList);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    const result: BreadcrumbItem[] = [
      { label: t('home'), link: '/' },
      { label: t('allCategories'), link: '/shop' },
    ];

    if (product.data?.categoryId) {
      const { categoryName, categorySlug } = product.data;
      result.push({
        label: truncate(categoryName, { length: 25 }),
        link: createCategoryUrl({ id: categorySlug }),
      });
    }

    if (product.data?.name) {
      result.push({
        label: truncate(product.data.name, { length: 80 }),
      });
    }

    return result;
  }, [product.data, t]);

  useStatusBarControl({
    initialColor: 'dark-content',
  });

  const pageTitle =
    product.selectedOffer?.metadataTitle ||
    product.data?.metadataTitle ||
    product.data?.name ||
    '';
  const pageDescription =
    product.selectedOffer?.metadataDescription ||
    product.data?.metadataDescription ||
    '';
  const canonical = company.config.canonical + (pathname ?? '');

  const loaderActive = product.request.isLoading || isLoading;
  const offerLoaderActive = loaderActive || product.isOfferLoading;

  const ratingData = getProductRatingData(product);

  if (product.notFound) {
    return <NotFoundV3 />;
  }

  const productTabs: TabItem[] = [
    isBundle
      ? {
          id: PRODUCT_TABS.listOfItems,
          content: <ListOfItems product={product} />,
        }
      : null,

    nutritionList.length > 0 ||
    badgeList.length > 0 ||
    characteristicsList.length > 0
      ? {
          id: PRODUCT_TABS.characteristics,
          content: (
            <div>
              {badgeList.length > 0 && (
                <Characteristics
                  propertyList={[]}
                  badgeList={badgeList}
                  showHeader={false}
                  labels={{
                    characteristic: t('characteristic'),
                    characteristicValue: t('characteristicValue'),
                    seeAll: t('seeAll'),
                    showLess: t('showLess'),
                  }}
                />
              )}
              {characteristicsList.length > 0 && (
                <>
                  <Typography.Text200Semibold
                    as="p"
                    className="text-text-primary"
                  >
                    {t('productInformation')}
                  </Typography.Text200Semibold>
                  <Characteristics
                    propertyList={characteristicsList}
                    showHeader={false}
                    borderless
                    labels={{
                      characteristic: t('characteristic'),
                      characteristicValue: t('characteristicValue'),
                      seeAll: t('seeAll'),
                      showLess: t('showLess'),
                    }}
                  />
                </>
              )}
              {nutritionList.length > 0 && (
                <>
                  <Typography.Text200Semibold
                    as="p"
                    className="text-text-primary"
                  >
                    {t('nutritionalInformation')}
                  </Typography.Text200Semibold>
                  <Characteristics
                    propertyList={nutritionList}
                    labels={{
                      characteristic: t('characteristic'),
                      characteristicValue: t('characteristicValue'),
                      extraHeader: t('productPage:riPer100g'),
                      seeAll: t('seeAll'),
                      showLess: t('showLess'),
                    }}
                  />
                </>
              )}
            </div>
          ),
        }
      : null,
    currentItem?.description
      ? {
          id: PRODUCT_TABS.description,
          content: (
            <CollapsibleText
              className={styles.description}
              buttonContainerClassName={styles.buttonContainer}
              text={currentItem.description}
              isLoading={product.request.isLoading}
              includeIcon={false}
              buttonText={{ more: 'readMore', less: 'readLess' }}
            />
          ),
        }
      : null,
    applicationProperty
      ? {
          id: PRODUCT_TABS.application,
          content: (
            <CollapsibleText
              className={styles.description}
              buttonContainerClassName={styles.buttonContainer}
              text={String(applicationProperty.value)}
              includeIcon={false}
              buttonText={{ more: 'readMore', less: 'readLess' }}
            />
          ),
        }
      : null,
    compositionProperty
      ? {
          id: PRODUCT_TABS.composition,
          content: (
            <CollapsibleText
              className={styles.description}
              buttonContainerClassName={styles.buttonContainer}
              text={String(compositionProperty.value)}
              includeIcon={false}
              buttonText={{ more: 'readMore', less: 'readLess' }}
            />
          ),
        }
      : null,
  ].filter(Boolean) as TabItem[];

  return (
    <>
      <Helmet>
        <title>
          {pageTitle && `${pageTitle} - `}
          {company.config.name}
        </title>
        <meta name="description" content={pageDescription} />
        <meta data-react-helmet="true" name="robots" content="index, follow" />
        <link data-react-helmet="true" rel="canonical" href={canonical} />
      </Helmet>

      <div className={cn(styles.root, isRN && styles.addAppGap)}>
        <div className={styles.container}>
          <Breadcrumbs
            items={breadcrumbItems}
            isLoading={loaderActive}
            className={styles.breadcrumbs}
          />

          <div className={styles.product}>
            <section>
              <ProductImageGallery
                isBundle={isBundle}
                bundleImages={bundleImages}
                currentItem={currentItem}
                product={product}
                handleFavorite={handleFavorite}
                isLoading={offerLoaderActive}
              />
            </section>
            <section className={styles.info}>
              <ProductTitle isLoading={offerLoaderActive}>{title}</ProductTitle>
              <ProductBadges
                isBundle={isBundle}
                currentItem={currentItem}
                isExpress={isExpress}
                className={styles.badges}
                isLoading={offerLoaderActive}
              />
              {company.isDubaiExpressCompany && (
                <ProductInfoBadges className={styles.infoBadges} />
              )}
              <ProductPrice
                currentItem={currentItem}
                currentPrice={currentPrice}
                isMultiBuy={isMultiBuy}
                isLoading={offerLoaderActive}
                count={count}
              />
              <ProductCustomsInfo
                currentItem={currentItem}
                isLoading={offerLoaderActive}
              />
              <ProductPickupEta />
              <ProductMultibuyLevels
                currentItem={currentItem}
                isMultiBuy={isMultiBuy}
                isLoading={offerLoaderActive}
                count={count}
              />
              {!isBundle && (
                <ProductProperties
                  className={styles.volumes}
                  product={product}
                  handleChangeOffer={handleChangeOffer}
                  isLoading={loaderActive}
                />
              )}
              {!isMobile && (
                <div className={styles.ctaArea}>
                  <AddToCardButton
                    handleChangeCount={handleChangeCount}
                    count={count}
                    limit={limit}
                    isLoading={offerLoaderActive}
                    product={product}
                    currentItem={currentItem}
                    isBundle={isBundle}
                    isMultiBuy={isMultiBuy}
                  />
                  <MiscButtons
                    product={product}
                    handleFavorite={handleFavorite}
                    isLoading={loaderActive}
                  />
                </div>
              )}
              {company.isDubaiExpressCompany && (
                <ProductUspTiles className={styles.uspTiles} />
              )}
              <Tabs
                key={product.data?.id}
                tabs={productTabs}
                defaultValue={
                  isBundle ? PRODUCT_TABS.listOfItems : PRODUCT_TABS.description
                }
                isLoading={offerLoaderActive}
                className={styles.tabs}
                tabsClassName="overscreen-4 overflow-x-auto overflow-y-hidden max-md:max-w-screen scrollbar-hide"
              />
            </section>
          </div>

          {company.useReviewsOnProductPage && (
            <div className={styles.reviewSection}>
              <div className={styles.reviewColumn}>
                <ProductReviews
                  reviews={product.reviews.list}
                  totalCount={
                    product.reviews.request.total || product.reviews.list.length
                  }
                  onLoadMore={product.reviews.request.loadMore}
                  canLoadMore={product.reviews.request.canLoadMore}
                  canCreateReview={product.reviews.canCreateReview}
                  onCreateReview={handleCreateReview}
                />
              </div>
              {ratingData && (
                <div className={styles.ratingColumn}>
                  <ProductRatingSummary
                    rating={ratingData.averageRating}
                    ratingData={ratingData}
                  />
                </div>
              )}
            </div>
          )}
          <ProductRecommendations
            title="similarProducts"
            source={RecommendationSource.CARD_SIMILAR}
            isLoading={loaderActive}
          />
        </div>
      </div>
    </>
  );
};

export default observer(ProductV3);
